// MCHD Room Scheduler - Reports Hook
// Custom hook for reports and analytics functionality

import { useMutation, useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from '../../api';

export interface RoomUtilizationParams {
  startDate?: string;
  endDate?: string;
  buildingId?: string;
  floorId?: string;
}

export interface UserActivityParams {
  startDate?: string;
  endDate?: string;
  role?: string;
  isActive?: boolean;
}

export interface SystemHealthParams {
  startDate?: string;
  endDate?: string;
}

export interface ExportParams {
  type: string;
  format: 'pdf' | 'csv' | 'excel';
  startDate?: string;
  endDate?: string;
}

export interface RoomUtilizationData {
  summary: {
    totalRooms: number;
    activeRooms: number;
    averageUtilization: number;
    totalSessions: number;
    completedSessions: number;
  };
  utilization: Array<{
    roomId: string;
    roomNumber: string;
    buildingName: string;
    floorName: string;
    utilization: number;
    totalSessions: number;
    completedSessions: number;
  }>;
  roomDetails: Array<{
    roomId: string;
    roomNumber: string;
    buildingName: string;
    floorName: string;
    status: string;
    capacity: number;
    specialtyTags: string[];
    utilization: number;
  }>;
  timeRange: {
    startDate: string;
    endDate: string;
  };
}

export interface UserActivityData {
  summary: {
    totalUsers: number;
    activeUsers: number;
    newUsers: number;
    loginAttempts: number;
    successfulLogins: number;
  };
  userActivity: Array<{
    userId: string;
    name: string;
    email: string;
    role: string;
    lastLogin: string;
    loginCount: number;
    isActive: boolean;
  }>;
  loginStats: Array<{
    date: string;
    attempts: number;
    successful: number;
    failed: number;
  }>;
  timeRange: {
    startDate: string;
    endDate: string;
  };
}

export interface SystemHealthData {
  summary: {
    uptime: number;
    totalRequests: number;
    errorRate: number;
    averageResponseTime: number;
    databaseStatus: string;
    redisStatus: string;
  };
  performance: Array<{
    timestamp: string;
    responseTime: number;
    requests: number;
    errors: number;
  }>;
  errors: Array<{
    timestamp: string;
    error: string;
    count: number;
    severity: string;
  }>;
  timeRange: {
    startDate: string;
    endDate: string;
  };
}

export interface AnalyticsOverview {
  utilization: {
    overall: number;
    byBuilding: Array<{ buildingId: string; buildingName: string; utilization: number }>;
    byFloor: Array<{ floorId: string; floorName: string; utilization: number }>;
  };
  users: {
    total: number;
    active: number;
    byRole: Array<{ role: string; count: number }>;
  };
  rooms: {
    total: number;
    active: number;
    byStatus: Array<{ status: string; count: number }>;
  };
  sessions: {
    total: number;
    completed: number;
    byType: Array<{ type: string; count: number }>;
  };
  lastUpdated: string;
}

export interface ExportResult {
  downloadUrl: string;
  filename: string;
  format: string;
  size: number;
  expiresAt: string;
}

/**
 * Get room utilization report
 * @param params - Report parameters
 * @returns Room utilization data
 */
const getRoomUtilizationReport = async (params: RoomUtilizationParams): Promise<RoomUtilizationData> => {
  const queryParams = new URLSearchParams();
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  if (params.buildingId) queryParams.append('buildingId', params.buildingId);
  if (params.floorId) queryParams.append('floorId', params.floorId);

  // api.get() returns JSON directly: { success: true, data: {...}, meta: {...} }
  const response = await api.get(`/reports/utilization?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get user activity report
 * @param params - Report parameters
 * @returns User activity data
 */
const getUserActivityReport = async (params: UserActivityParams): Promise<UserActivityData> => {
  const queryParams = new URLSearchParams();
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  if (params.role) queryParams.append('role', params.role);
  if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

  // api.get() returns JSON directly: { success: true, data: {...}, meta: {...} }
  const response = await api.get(`/reports/users?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get system health report
 * @param params - Report parameters
 * @returns System health data
 */
const getSystemHealthReport = async (params: SystemHealthParams): Promise<SystemHealthData> => {
  const queryParams = new URLSearchParams();
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);

  // api.get() returns JSON directly: { success: true, data: {...}, meta: {...} }
  const response = await api.get(`/reports/health?${queryParams.toString()}`);
  return response.data;
};

/**
 * Export report
 * @param params - Export parameters
 * @returns Export result
 */
const exportReport = async (params: ExportParams): Promise<ExportResult> => {
  const queryParams = new URLSearchParams();
  queryParams.append('type', params.type);
  queryParams.append('format', params.format);
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);

  // api.get() returns JSON directly: { success: true, data: {...}, meta: {...} }
  const response = await api.get(`/reports/export?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get analytics overview
 * @returns Analytics overview data
 */
const getAnalyticsOverview = async (): Promise<AnalyticsOverview> => {
  // api.get() returns JSON directly: { success: true, data: {...}, meta: {...} }
  const response = await api.get('/reports/analytics');
  return response.data;
};

/**
 * Custom hook for room utilization reports
 * @param params - Report parameters
 * @returns Room utilization report state and functions
 */
export function useRoomUtilizationReport(params: RoomUtilizationParams) {
  return useQuery({
    queryKey: ['room-utilization-report', params],
    queryFn: () => getRoomUtilizationReport(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: true,
  });
}

/**
 * Custom hook for user activity reports
 * @param params - Report parameters
 * @returns User activity report state and functions
 */
export function useUserActivityReport(params: UserActivityParams) {
  return useQuery({
    queryKey: ['user-activity-report', params],
    queryFn: () => getUserActivityReport(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: true,
  });
}

/**
 * Custom hook for system health reports
 * @param params - Report parameters
 * @returns System health report state and functions
 */
export function useSystemHealthReport(params: SystemHealthParams) {
  return useQuery({
    queryKey: ['system-health-report', params],
    queryFn: () => getSystemHealthReport(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: true,
  });
}

/**
 * Custom hook for analytics overview
 * @returns Analytics overview state and functions
 */
export function useAnalyticsOverview() {
  return useQuery({
    queryKey: ['analytics-overview'],
    queryFn: getAnalyticsOverview,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

/**
 * Custom hook for report export
 * @returns Export mutation and state
 */
export function useReportExport() {

  return useMutation({
    mutationFn: exportReport,
    onSuccess: (data) => {
      notifications.show({
        title: 'Export Successful',
        message: `Report exported as ${data.format.toUpperCase()}`,
        color: 'green',
      });
      
      // Trigger download with full API URL
      const fullDownloadUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:7991'}${data.downloadUrl}`;
      const link = document.createElement('a');
      link.href = fullDownloadUrl;
      link.download = data.filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Export Failed',
        message: error.response?.data?.error?.message || 'Failed to export report',
        color: 'red',
      });
    },
  });
}

/**
 * Custom hook for all reports functionality
 * @returns Combined reports state and functions
 */
export function useReports() {
  const analyticsQuery = useAnalyticsOverview();
  const exportMutation = useReportExport();

  const generateRoomUtilizationReport = (params: RoomUtilizationParams) => {
    return getRoomUtilizationReport(params);
  };

  const generateUserActivityReport = (params: UserActivityParams) => {
    return getUserActivityReport(params);
  };

  const generateSystemHealthReport = (params: SystemHealthParams) => {
    return getSystemHealthReport(params);
  };

  const exportReportData = (params: ExportParams) => {
    return exportMutation.mutateAsync(params);
  };

  const refetchAnalytics = () => {
    analyticsQuery.refetch();
  };

  return {
    // Analytics
    analytics: analyticsQuery.data,
    analyticsLoading: analyticsQuery.isLoading,
    analyticsError: analyticsQuery.error,
    refetchAnalytics,

    // Export
    exportReport: exportReportData,
    isExporting: exportMutation.isPending,
    exportError: exportMutation.error,

    // Report generators
    generateRoomUtilizationReport,
    generateUserActivityReport,
    generateSystemHealthReport,
  };
}
